import React, { useRef, useState, useCallback, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import Image from 'components/Image';
import { images } from 'utils/icons';
import { ReactComponent as EditIcon } from 'images/new/edit.svg';
import { resize } from 'utils/imageResizer';
import ReactCrop from 'react-image-crop';
import InputLabel from '@material-ui/core/InputLabel';
import staticText from 'static/text_display.json';
import 'react-image-crop/dist/ReactCrop.css';
import { IconButton, SvgIcon } from '@material-ui/core';
import { BorderedCloseIcon, SaveIcon } from 'Icons';
import { FORM, MASK } from 'utils/constants/css';
import { useTranslation } from 'react-i18next';
import { formatMessage } from 'utils/methods/helpers';
import cn from 'classnames';

const useStyles = makeStyles(theme => ({
  imageLabel: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
    marginBottom: '0.5rem',
    display: 'flex',
    flexDirection: 'row',
  },
  input: {
    display: 'none',
  },
  label: {
    borderBottom: 0,
  },
  container: {
    height: '20rem',
    width: '20rem',
    position: 'relative',
    border: FORM.BORDER,
    borderRadius: FORM.BORDER_RADIUS_IMAGE,
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
  },
  smallContainer: {
    height: '10rem',
    width: '10rem',
    position: 'relative',
    border: FORM.BORDER,
    borderRadius: FORM.BORDER_RADIUS_IMAGE,
    display: 'flex',
    flexDirection: 'column-reverse',
    alignItems: 'flex-end',
  },
  image: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    margin: '0 !important',
  },

  icon: {
    zIndex: 499,
    position: 'absolute',
    right: '0',
    bottom: '0',
    opacity: '0.9',
    height: '25px',
    width: '25px',
    borderTop: FORM.BORDER,
    borderLeft: FORM.BORDER,
    borderRadius: FORM.BORDER_RADIUS_IMAGE,
    background: `${theme.palette.text.secondary} !important`,
    fill: `${theme.palette.text.primary} !important`,
  },

  cropContainer: {
    position: 'fixed',
    top: 0,
    bottom: '10%',
    left: 0,
    right: 0,
    margin: 'auto',
    zIndex: 50000,
    width: '65em', // This is the actual width of the react portal we use in this app
    height: '600px',
    flexDirection: 'column',
    color: theme.palette.secondary.light,
  },
  cropButtons: {
    width: '40%',
    display: 'flex',
    justifyContent: 'flex-start',
    padding: '1rem',
    flexDirection: 'row-reverse',
  },
  error: {
    color: '#f44336',
    marginTop: '0.5rem',
    marginLeft: '0.5rem',
    fontSize: '10px',
  },
  header: {
    display: 'flex',
    // width: '100%',
    background: theme.palette.primary.main,
    justifyContent: 'space-between',
    top: '0px',
    alignItems: 'center',
    padding: '5px 15px',
    height: '63px',
  },
  canvas: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '65rem',
    background: 'rgba(0, 0, 0, 0.9)',
  },
  mySvgStyle: {
    height: '25px',
    width: '25px',
    cursor: 'pointer',
    '& [id="Layer_1"]': {
      color: `${theme.palette.secondary.light} !important`,
      '& [class*="st0"]': {
        fill: `${theme.palette.secondary.light} !important`,
      },
    },
  },
  mask: {
    position: 'absolute',
    opacity: MASK.OPACITY_LOW,
    left: 0,
    height: 'inherit',
    width: 'inherit',
    background: theme.palette.text.disabled,
    zIndex: 1000,
  },
}));

const originalDimensions = {
  unit: '%',
  width: 50,
  height: 50,
  x: 25,
  y: 25,
};

const ImageUpload = ({
  onChange,
  value,
  type,
  error,
  id,
  isImageMandatory,
  label = 'Image',
  showLabel = true,
  disabled = false,
  smallContainer = false,
}) => {
  const inputFile = useRef(null);
  const classes = useStyles();
  const [upImg, setUpImg] = useState();
  const [cropping, setCropping] = useState(false);
  const [crop, setCrop] = useState(originalDimensions);
  const imgRef = useRef(null);
  const { t } = useTranslation();
  const onLoad = useCallback(img => {
    imgRef.current = img;
  }, []);

  useEffect(() => {
    return () => {
      setUpImg(null);
    };
  }, []);
  const makeClientCrop = () => {
    if (imgRef && crop.width && crop.height) {
      onChange(createCropPreview(imgRef.current, crop));
      setCropping(false);
      setUpImg(null);
      setCrop(originalDimensions);
    }
  };

  const createCropPreview = (image, crop) => {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = Math.round(crop.width);
    canvas.height = Math.round(crop.height);
    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      imgRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      Math.round(crop.width),
      Math.round(crop.height),
    );

    return canvas.toDataURL('image/jpeg', 1);
  };

  const handleCapture = ({ target }) => {
    if (target.files.length > 0) {
      resize({
        file: target.files[0],
        maxWidth: window.innerWidth - 100,
        maxHeight: window.innerHeight - 100,
        fn: setUpImg,
      });
      setCropping(true);
      setCrop(originalDimensions);
      target.value = '';
    }
  };

  const handleClick = () => {
    inputFile.current.click();
  };

  return (
    <>
      <InputLabel
        shrink
        className={classes.imageLabel}
        required={isImageMandatory}
        htmlFor="component-error"
      >
        {showLabel && t(label)}
        {error && <span className={classes.error}>{t(error)}</span>}
      </InputLabel>

      <div
        className={cn(classes.container, {
          [classes.smallContainer]: smallContainer,
        })}
      >
        {disabled && <div className={classes.mask}></div>}
        <input
          accept="image/*"
          className={classes.input}
          id={id}
          ref={inputFile}
          onChange={!disabled && handleCapture}
          type="file"
        />
        <Image
          src={value ? value : images[`empty${type}`].default}
          className={classes.image}
          onClick={!disabled && handleClick}
          title={t(
            formatMessage({
              message: value
                ? staticText.general.replace_image
                : staticText.general.add_image,
            }),
          )}
        />

        <IconButton
          color="secondary"
          component="div"
          onClick={!disabled && handleClick}
          title={t(
            formatMessage({
              message: value
                ? staticText.general.replace_image
                : staticText.general.add_image,
            }),
          )}
        >
          <SvgIcon className={classes.icon}>
            <EditIcon className={classes.img} alt={'yesicon'} />
          </SvgIcon>
        </IconButton>
      </div>
      {cropping && upImg && (
        <div className={classes.cropContainer}>
          <div
            className={classes.header}
            // style={{ width: imgRef.current?.clientWidth - 2 * EXTRA_PADDING }}
          >
            <h2>{t('Crop Image')}</h2>
            <section className={classes.cropButtons}>
              <IconButton
                onClick={() => {
                  setCropping(false);
                  setCrop(
                    // set back the original dimensions
                    originalDimensions,
                  );
                }}
                title={t('Discard and Close')}
                size="small"
              >
                <SvgIcon className={classes.mySvgStyle}>
                  <BorderedCloseIcon />
                </SvgIcon>
              </IconButton>
              <IconButton
                onClick={makeClientCrop}
                title={t('Update and Close')}
                size="small"
              >
                <SvgIcon className={classes.mySvgStyle}>
                  <SaveIcon />
                </SvgIcon>
              </IconButton>
            </section>
          </div>
          <div className={classes.canvas}>
            <ReactCrop
              src={upImg}
              onImageLoaded={onLoad}
              crop={crop}
              style={{
                height: '100%',
              }}
              onChange={c => setCrop(c)}
            />
          </div>
        </div>
      )}
    </>
  );
};

ImageUpload.propTypes = {
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  type: PropTypes.oneOf(['Profile', 'Establishment']),
  onChange: PropTypes.func.isRequired,
  value: PropTypes.string,
  id: PropTypes.string,
  isImageMandatory: PropTypes.bool,
  label: PropTypes.string,
  showLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  smallContainer: PropTypes.bool,
};

ImageUpload.defaultProps = {
  type: 'Profile',
  isImageMandatory: true,
};

export default ImageUpload;
